import { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import CTLTable from "application/components/CTLTable";
import CTLNotification from "application/components/CTLNotification";
import { getColumns, Attributes, operationURLS } from "./model";
import { application } from "globals/endpoints";

// Scheduled Reports / Generated Reports (task #9574). Lists pooled billing reports from the
// report_type-scoped /pdfReport/scheduled/list endpoint and downloads a completed report by id.
// Does not touch the Voice Logger / Backup Download page.
export default function ScheduledReports({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [updateList, setUpdateList] = useState(Date.now());
  const [selectedRows, setSelectedRows] = useState([]);
  const [sending, setSending] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  // registerCallBacks only (re)registers when pageId changes, so the registered onSendEmail
  // closure is captured once; read selection/in-flight state through refs (mirrors the
  // selectedRowsRef pattern in broadcasts/message) so it always sees the latest values.
  const selectedRowsRef = useRef([]);
  const sendingRef = useRef(false);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);

  function onRefresh() {
    setUpdateList(Date.now());
  }

  function onRowSelectionChange(rows) {
    setSelectedRows(rows || []);
  }

  // Resend the delivery email for the currently selected rows (task #9879). Reuses the
  // already-generated PDF via the backend's resendEmail endpoint — no new generation, no
  // data re-query. Non-success / no-recipient rows are skipped client-side and reported in
  // the summary toast; the Email toolbar button is guarded against re-entry while a batch is
  // in flight to avoid a double-submit.
  function onResendClick() {
    if (sendingRef.current) {
      return;
    }
    const selected = selectedRowsRef.current;
    if (!selected.length) {
      setNotify({
        isOpen: true,
        pagename: pageName,
        type: "error",
        status: "Select one or more successfully generated reports to resend.",
      });
      return;
    }

    // Mirrors the backend eligibility gate exactly (status/sendEmail/emailId) so an
    // ineligible row is reported as skipped here instead of round-tripping to the server.
    const eligible = selected.filter(
      (row) =>
        row.status === "success" &&
        row.sendEmail === true &&
        row.emailId &&
        row.emailId.trim() !== ""
    );
    const skipped = selected.length - eligible.length;

    if (!eligible.length) {
      setNotify({
        isOpen: true,
        pagename: pageName,
        type: "error",
        status: "Selected report(s) are not eligible for resend (must be successfully generated with a recipient email).",
      });
      return;
    }

    setSending(true);
    sendingRef.current = true;
    Promise.allSettled(
      eligible.map((row) => application.post(`${operationURLS.resendEmail}/${row.id}`))
    ).then((results) => {
      const queued = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - queued;
      setUpdateList(Date.now());
      setSelectedRows([]);
      setSending(false);
      setNotify({
        isOpen: true,
        pagename: pageName,
        type: failed ? "error" : "success",
        status:
          `${queued} email(s) queued for resend` +
          (failed ? `, ${failed} failed to queue` : "") +
          (skipped ? `, ${skipped} skipped (not eligible)` : ""),
      });
    });
  }

  useEffect(() => {
    registerCallBacks &&
      registerCallBacks({
        UpdateTableRecords: onRefresh,
        onRefresh: onRefresh,
        onSendEmail: onResendClick,
      });
  }, [pageId]);

  // Hide the Email (resend) toolbar button while a resend batch is in flight — CTLNavBar has
  // no separate "disabled" affordance for this slot, but re-driving onLoad toggles the same
  // enableEmailButton flag it already renders conditionally on, which is sufficient to block
  // a double-submit.
  useEffect(() => {
    Attributes && onLoad && onLoad({ ...Attributes, enableEmailButton: !sending });
  }, [pageId, sending]);

  function downloadReport(reportId) {
    application
      .get(`${operationURLS.reportDownload}/${reportId}`, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Scheduled-Report-${reportId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        setNotify({
          isOpen: true,
          pagename: pageName,
          type: "success",
          status: "Report download successful",
        });
      })
      .catch(() => {
        setNotify({
          isOpen: true,
          pagename: pageName,
          type: "error",
          status: "Unable to download report.",
        });
      });
  }

  return (
    <MDBox>
      <CTLTable
        apiURL={operationURLS.list}
        columns={getColumns(downloadReport)}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="desc"
        defaultSortColumn="createdOnDate"
        pageId={pageId}
        searchText={searchText}
        hasRowSelection={true}
        onRowSelectionChange={onRowSelectionChange}
        updateList={updateList}
        defaultPageSize={20}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
